import React from 'react';
import { ActiveFilter, FilterProps } from './interfaces';
import styles from './Filter.module.scss';
import FormField from '../FormField/FormField';
import { FormFieldType } from '../FormField/consts';
import MultiSelect from '../MultiSelect/MultiSelect';
import Icon from '../Icon/Icon';
import clsx from 'clsx';
import AccordionItem from '../AccordionItem/AccordionItem';
import CheckBox from '../CheckBox/CheckBox';
import { ClickableAction } from '../Clickable/consts';
import { ButtonWidth } from '../Button/consts';
import Button from '../Button/Button';
import Pill from '../Pill/Pill';
import LockBodyScroll from '../LockBodyScroll/LockBodyScroll';

const Filter = ({
  searchTerm = '',
  onSearchTermChange,
  onFilterReset,
  filterItems = [],
  isBoxOnGradient = false,
  onFilterApplyClick,
}: FilterProps) => {
  const [isMobileFilterOpen, setIsMobileFilterOpen] = React.useState(false);
  const activeFilters: ActiveFilter[] = filterItems.flatMap((filterItem) =>
    filterItem.selected.map((selectedItem) => ({
      title: selectedItem,
      onClose: () => filterItem.toggleSelected(selectedItem),
    }))
  );

  const filterText = activeFilters.length
    ? `${activeFilters.length} Filter angewendet`
    : 'Filtern';

  const toggleMobileFilter = () => {
    setIsMobileFilterOpen(!isMobileFilterOpen);
  };

  return (
    <div className={styles.base}>
      <div className={styles.filterWrapper}>
        <form
          onSubmit={($event) => {
            $event.preventDefault();

            onFilterApplyClick();
          }}
        >
          <FormField
            name="searchTerm"
            label="Job oder Schlagwort suchen"
            type={FormFieldType.Text}
            onChange={onSearchTermChange}
            icon={'action/basic-search'}
            iconSize={'iconSize20'}
            onBlur={onFilterApplyClick}
            className={styles.searchTermInput}
            isBoxOnGradient={isBoxOnGradient}
            value={searchTerm}
            onClickIcon={onFilterApplyClick}
            isIconHightlighted
          />
        </form>

        <div className={styles.filter}>
          {filterItems.map((filterItem) => {
            return (
              <MultiSelect
                {...filterItem}
                key={filterItem.label}
                className={styles.multiSelect}
                isBoxOnGradient={isBoxOnGradient}
              />
            );
          })}
        </div>
      </div>

      {activeFilters.length > 0 && (
        <div className={styles.filterPills}>
          {activeFilters.map((activeFilter) => {
            return (
              <Pill
                key={activeFilter.title}
                title={activeFilter.title}
                onClose={activeFilter.onClose}
                isBoxOnGradient={isBoxOnGradient}
              />
            );
          })}

          <div
            className={clsx(styles.resetButton, {
              [styles.withGradient]: isBoxOnGradient,
            })}
            onClick={onFilterReset}
          >
            Alle Filter löschen
          </div>
        </div>
      )}

      <button
        className={clsx(styles.mobileFilterButton, {
          [styles.inverted]: isBoxOnGradient,
        })}
        onClick={toggleMobileFilter}
      >
        <Icon variant={'user/item-list'} size="iconSize24" />
        <span className={styles.text}>{filterText}</span>
      </button>

      {isMobileFilterOpen && <LockBodyScroll />}
      <div
        className={clsx(styles.filterModal, {
          [styles.filterModalActive]: isMobileFilterOpen,
        })}
      >
        <div className={styles.modalHeadline}>
          <div className={styles.title}>Filtereinstellungen</div>
          <Icon
            variant={'action/close'}
            size="iconSize24"
            className={styles.icon}
            onClick={toggleMobileFilter}
          />
        </div>
        <div className={styles.filterModalContent}>
          <div className={styles.filterMobile}>
            {filterItems.map((filterItem) => {
              return (
                <AccordionItem
                  key={filterItem.label}
                  headline={`${filterItem.label} (${filterItem.numberOfSelected})`}
                >
                  {filterItem.items.map((checkboxItem) => {
                    return (
                      <div
                        key={checkboxItem.label}
                        className={styles.checkBoxItem}
                        onClick={() =>
                          filterItem.toggleSelected(checkboxItem.label)
                        }
                      >
                        <CheckBox
                          checked={filterItem.isSelected(checkboxItem.label)}
                          readOnly
                        />
                        <span
                          className={clsx(styles.text, {
                            [styles.active]: filterItem.isSelected(
                              checkboxItem.label
                            ),
                          })}
                        >
                          {checkboxItem.label}
                          {checkboxItem.count?.toString()
                            ? ` (${checkboxItem.count})`
                            : ''}
                        </span>
                      </div>
                    );
                  })}
                </AccordionItem>
              );
            })}
          </div>
          <div className={styles.mobileActionButtons}>
            <Button
              actionType={ClickableAction.Custom}
              width={ButtonWidth.Full}
              onClick={() => {
                toggleMobileFilter();
                onFilterApplyClick();
              }}
            >
              Filter anwenden
            </Button>
            <button
              onClick={() => {
                onFilterReset();
                toggleMobileFilter();
              }}
              className={styles.mobileResetButton}
            >
              Alle Filter löschen
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Filter;
